import { useState } from 'react';
import { X, Loader2, AlertCircle, User, MapPin, FileText, Globe, Lock, Users } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, type ProfileVisibility } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { AvatarUpload } from './AvatarUpload';
import { queryKeys } from '../lib/queryKeys';
import type { UserSettings } from '../hooks/useSettingsQuery';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  profile: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    bio: string | null;
    location: string | null;
    is_public: boolean;
    visibility: ProfileVisibility;
  };
}

export function EditProfileModal({ isOpen, onClose, onSave, profile }: EditProfileModalProps) {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(profile.display_name || '');
  const [bio, setBio] = useState(profile.bio || '');
  const [location, setLocation] = useState(profile.location || '');
  // Use visibility as source of truth, fall back to is_public for legacy profiles
  const [visibility, setVisibility] = useState<ProfileVisibility>(
    profile.visibility || (profile.is_public ? 'public' : 'private')
  );
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to edit your profile');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log('[EditProfile] Saving profile...', { userId: user.id });
      const token = session?.access_token;
      if (!token) {
        setError('Session expired. Please sign in again.');
        return;
      }

      // Update profile text fields via worker endpoint.
      // Avatar is handled separately by /api/upload/avatar (fires identifyUser on its own).
      await apiFetch('/user/profile', {
        method: 'PATCH',
        token,
        body: {
          display_name: displayName.trim() || null,
          bio: bio.trim() || null,
          location: location.trim() || null,
        },
      });

      // Update visibility via settings endpoint (separate concern)
      const currentVisibility = profile.visibility || (profile.is_public ? 'public' : 'private');
      if (visibility !== currentVisibility) {
        await apiFetch('/user/settings', {
          method: 'PATCH',
          token,
          body: { visibility },
        });
      }

      // Cross-write visibility to both caches for instant UI sync (global refetchOnMount:false)
      // 1) Profile cache - update visibility fields
      const profileKey = queryKeys.profile.byUsername(profile.username.toLowerCase());
      queryClient.setQueryData(profileKey, (prev: unknown) => {
        if (!prev || typeof prev !== 'object') return prev;
        const prevProfile = prev as Record<string, unknown>;
        return {
          ...prevProfile,
          visibility: visibility,
          is_public: visibility === 'public',
        };
      });

      // 2) Settings cache - update visibility if cache exists
      queryClient.setQueryData<UserSettings>(['settings', user.id], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          visibility: visibility,
        };
      });

      console.log('[EditProfile] Save successful, closing modal');
      onSave();
    } catch (err) {
      console.error('[EditProfile] Error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0A0A]/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#0F1419] rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl shadow-black/50 ring-1 ring-white/[0.06] max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white">Edit Profile</h2>
          <p className="text-slate-400 text-sm mt-1">Update your profile information</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Avatar Upload */}
          <div className="flex justify-center pb-2">
            <AvatarUpload
              currentAvatarUrl={avatarUrl}
              onUploadComplete={(url) => setAvatarUrl(url)}
            />
          </div>

          {/* Display Name */}
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-slate-300 mb-1">
              Display Name
            </label>
            <div className="relative">
              <User
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={profile.username}
                maxLength={50}
                className="w-full pl-10 pr-4 py-2.5 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              This is how your name will appear to others. Leave empty to use your username.
            </p>
          </div>

          {/* Bio */}
          <div>
            <label htmlFor="bio" className="block text-sm font-medium text-slate-300 mb-1">
              Bio
            </label>
            <div className="relative">
              <FileText
                size={18}
                className="absolute left-3 top-3 text-slate-500"
              />
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                maxLength={160}
                rows={3}
                className="w-full pl-10 pr-4 py-2.5 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors resize-none"
              />
            </div>
            <p className="mt-1 text-xs text-slate-500 text-right">
              {bio.length}/160
            </p>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-slate-300 mb-1">
              Location
            </label>
            <div className="relative">
              <MapPin
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                id="location"
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, Country"
                maxLength={100}
                className="w-full pl-10 pr-4 py-2.5 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              />
            </div>
          </div>

          {/* Profile Visibility Radio */}
          <div className="pt-2 border-t border-white/[0.06]">
            <p className="text-sm font-medium text-slate-300 mb-3">Profile Visibility</p>
            <div className="space-y-2">
              {/* Public */}
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                visibility === 'public' ? 'bg-cyan-500/10 ring-1 ring-cyan-500/50' : 'bg-[#1A1F26] ring-1 ring-white/[0.08] hover:bg-[#1F252C]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                  className="sr-only"
                />
                <Globe size={20} className={visibility === 'public' ? 'text-cyan-400' : 'text-slate-500'} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Public</p>
                  <p className="text-xs text-slate-400">Anyone can see your profile</p>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  visibility === 'public' ? 'border-cyan-500 bg-cyan-500' : 'border-slate-500'
                }`}>
                  {visibility === 'public' && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                </div>
              </label>

              {/* Friends Only */}
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                visibility === 'friends' ? 'bg-cyan-500/10 ring-1 ring-cyan-500/50' : 'bg-[#1A1F26] ring-1 ring-white/[0.08] hover:bg-[#1F252C]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="friends"
                  checked={visibility === 'friends'}
                  onChange={() => setVisibility('friends')}
                  className="sr-only"
                />
                <Users size={20} className={visibility === 'friends' ? 'text-cyan-400' : 'text-slate-500'} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Friends Only</p>
                  <p className="text-xs text-slate-400">Only mutual followers can see your full profile</p>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  visibility === 'friends' ? 'border-cyan-500 bg-cyan-500' : 'border-slate-500'
                }`}>
                  {visibility === 'friends' && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                </div>
              </label>

              {/* Private */}
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                visibility === 'private' ? 'bg-cyan-500/10 ring-1 ring-cyan-500/50' : 'bg-[#1A1F26] ring-1 ring-white/[0.08] hover:bg-[#1F252C]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                  className="sr-only"
                />
                <Lock size={20} className={visibility === 'private' ? 'text-cyan-400' : 'text-slate-500'} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Private</p>
                  <p className="text-xs text-slate-400">Only you can see your full profile</p>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  visibility === 'private' ? 'border-cyan-500 bg-cyan-500' : 'border-slate-500'
                }`}>
                  {visibility === 'private' && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                </div>
              </label>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Your username, avatar, and follower counts are always visible.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-slate-300 font-medium rounded-lg ring-1 ring-white/[0.08] hover:bg-[#1A1F26] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-400 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {saving && <Loader2 size={18} className="animate-spin" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
